import { useCallback, useEffect, useRef, useState } from "react";

export interface NominatimCandidate {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address: {
    road?: string;
    house_number?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    suburb?: string;
    country_code?: string;
  };
}

/** Formatiert eine Adresse als "Straße HNr, PLZ Ort" */
export function formatAddress(c: NominatimCandidate): string {
  const { road, house_number, postcode, city, town, village, municipality } = c.address;
  const street = road ? (house_number ? `${road} ${house_number}` : road) : null;
  const place = city ?? town ?? village ?? municipality ?? null;
  if (street && postcode && place) return `${street}, ${postcode} ${place}`;
  if (street && place) return `${street}, ${place}`;
  if (postcode && place) return `${postcode} ${place}`;
  return c.display_name;
}

interface MapTilerFeature {
  id: string;
  place_name: string;
  text?: string; // street name when address kind
  center: [number, number]; // [lon, lat]
  relevance?: number; // 0..1 match quality
  properties?: Record<string, string | undefined>;
  address?: string; // house number for address-kind features
  context?: { id: string; text: string }[];
}

/** Maps a MapTiler feature to NominatimCandidate. Prefers MapTiler's
 *  structured fields over regex-parsing place_name, since the regex misses
 *  letter-suffix housenumbers ("4a"), missing-housenumber streets, etc. */
function toCandidate(f: MapTilerFeature, idx: number): NominatimCandidate {
  const ctx = f.context ?? [];
  const findCtx = (prefix: string) => ctx.find((c) => c.id.startsWith(prefix))?.text;

  const postcode = findCtx("postcode") ?? f.properties?.postcode;
  const city = findCtx("place") ?? findCtx("municipality") ?? f.properties?.city;

  // Prefer structured address.* if MapTiler returned it (kind=address features
  // have `text`=street and `address`=housenumber). Fall back to place_name
  // regex only when text is missing.
  let road: string | undefined;
  let house_number: string | undefined;
  if (f.text) {
    road = f.text;
    house_number = f.address ?? undefined;
  } else {
    const firstPart = f.place_name.split(",")[0]?.trim() ?? "";
    const streetMatch = firstPart.match(/^(.+?)\s+(\d+\S*)$/);
    road = streetMatch ? streetMatch[1] : firstPart;
    house_number = streetMatch ? streetMatch[2] : undefined;
  }

  return {
    place_id: idx + 1,
    display_name: f.place_name,
    lat: String(f.center[1]),
    lon: String(f.center[0]),
    address: {
      road,
      house_number,
      postcode,
      city,
      country_code: "de",
    },
  };
}

/** Pull postcode (5-digit DE) and house number out of a free-text query so we
 *  can re-rank geocoder results that don't put the best match first. */
function parseQueryHints(q: string): { postcode?: string; houseNumber?: string } {
  const postcode = q.match(/\b(\d{5})\b/)?.[1];
  // "Mozartstraße 4" or "...4a" — take the first standalone number(+letter)
  // that isn't a 5-digit PLZ.
  const tokens = q.split(/[\s,]+/);
  let houseNumber: string | undefined;
  for (const t of tokens) {
    if (/^\d{5}$/.test(t)) continue;
    if (/^\d+[a-zA-Z]?$/.test(t)) { houseNumber = t; break; }
  }
  return { postcode, houseNumber };
}

/** Score how well a candidate matches the user's query. Higher = better. */
function scoreCandidate(c: NominatimCandidate, hints: ReturnType<typeof parseQueryHints>): number {
  let s = 0;
  if (hints.postcode && c.address.postcode === hints.postcode) s += 100;
  if (hints.houseNumber && c.address.house_number?.toLowerCase() === hints.houseNumber.toLowerCase()) s += 50;
  if (c.address.house_number) s += 10; // prefer house-level over street-level
  return s;
}

export function useNominatim(options?: { disabled?: boolean }) {
  const disabled = options?.disabled ?? false;
  const [query, setQuery] = useState("");
  const [trigger, setTrigger] = useState(0);
  const [candidates, setCandidates] = useState<NominatimCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const tokenRef = useRef(0);

  const search = useCallback((q: string) => {
    setQuery(q);
  }, []);

  const forceSearch = useCallback((q: string) => {
    setQuery(q);
    setTrigger((t) => t + 1);
  }, []);

  useEffect(() => {
    if (disabled) {
      setCandidates([]);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    if (query.trim().length < 3) {
      setCandidates([]);
      return;
    }

    const myToken = ++tokenRef.current;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setLoading(true);

    const run = async () => {
      // Microtask-debounce: if another search has been triggered since this one
      // was scheduled, drop this run. This still collapses rapid React-batched
      // updates while letting tests observe the fetch synchronously after `act`.
      if (myToken !== tokenRef.current) return;
      if (disabled) { setLoading(false); return; }
      try {
        // Normalize street abbreviations before geocoding (DACH-04)
        const normalizedQuery = query
          .replace(/\bStr\.\b/g, "Straße")
          .replace(/\bstr\.\b/g, "straße")
          .trim();
        const q = encodeURIComponent(normalizedQuery);
        const url = `/api/maps/geocode?q=${q}&language=de&country=de&limit=5`;

        const res = await fetch(url, { signal });
        if (!res.ok) throw new Error(`Geocode HTTP ${res.status}`);

        const data = await res.json();
        // Backend can return either MapTiler (`{features:[]}` when MAPTILER_API_KEY is set)
        // or Nominatim (flat array, fallback). Detect and handle both.
        const cands: NominatimCandidate[] = Array.isArray(data)
          ? (data as NominatimCandidate[])
          : ((data.features ?? []) as MapTilerFeature[]).map(toCandidate);
        // Re-rank by user-input hints (postcode + house number) so the best
        // semantic match wins even if MapTiler ranks differently.
        const hints = parseQueryHints(query);
        cands.sort((a, b) => scoreCandidate(b, hints) - scoreCandidate(a, hints));
        if (myToken === tokenRef.current) setCandidates(cands);
      } catch (err) {
        if ((err as Error).name !== "AbortError" && myToken === tokenRef.current) {
          setCandidates([]);
        }
      } finally {
        if (myToken === tokenRef.current) setLoading(false);
      }
    };
    timerRef.current = setTimeout(() => void run(), trigger > 0 ? 0 : 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, trigger, disabled]);

  const clear = useCallback(() => {
    setCandidates([]);
    setQuery("");
  }, []);

  return { query, search, forceSearch, candidates, loading, clear };
}
